'use client';

import { LineChart, LineChartData } from '@/components/charts/LineChart';
import { theme } from '@/config/theme';
import React from 'react';
import styled from 'styled-components';
import { TimeWindowButtons, type DrilldownTableFilters } from '@/components/tables/DrilldownTable';

interface ResponseTimeMetrics {
  average: number;
  minimum: number;
  maximum: number;
  totalChecks?: number;
}

interface ResponseTimeChartProps {
  chartData: LineChartData;
  metrics: ResponseTimeMetrics;
  timeRange?: string;
  onTimeRangeChange?: (value: string) => void;
  className?: string;
  title?: string;
  subtitle?: string;
  response_time_threshold?: number;
  formatYAxisTicks?: boolean; // If true, format ticks with ms/s units
  yAxisLabel?: string;
  tooltipLabel?: string;
  tooltipFormatter?: (value: number) => string;
  yAxisTickFormatter?: (value: number) => string;
  metricFormatter?: (value: number) => string;
  showTimeRangeDropdown?: boolean; // Make dropdown optional
  timeWindowButtons?: React.ReactNode; // Time window buttons component
  dateRangePicker?: React.ReactNode; // Date range picker component
  loading?: boolean;
  onFullscreenChange?: (isFullscreen: boolean) => void;
}

const Container = styled.div`
  background: ${theme.colors.white};
  border: 1px solid ${theme.colors.border.primary};
  border-radius: 8px;
  padding: 8px 0 24px;
`;


const ContentGrid = styled.div`
  /* Stacked by default for all screen sizes */
  display: flex;
  flex-direction: column;

  /* Two-column layout on ultra-wide screens (2xl and above) */
  @media (min-width: ${theme.breakpoints['2xl']}) {
    display: grid;
    gap: 24px;
    align-items: start;
  }
`;

const ChartWrapper = styled.div`
  margin-bottom: 12px;
  position: relative;
  overflow-x: auto;
  overflow-y: hidden;
  width: 100%;
  
  /* Remove max-width constraint on ultra-wide screens to fill left column */
  @media (min-width: ${theme.breakpoints['2xl']}) {
    max-width: none;
    margin-bottom: 0;
  }
  
  /* Custom scrollbar styling */
  &::-webkit-scrollbar {
    height: 8px;
  }
  
  &::-webkit-scrollbar-track {
    background: #f3f4f6;
    border-radius: 4px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: #d1d5db;
    border-radius: 4px;
    
    &:hover {
      background: ${theme.colors.gray[400]};
    }
  }
  
  /* Prevent the chart from making the page scroll */
  & > div {
    display: inline-block;
    min-width: 100%; 
  }
`;

const MetricsGrid = styled.div<{ $hasTotal?: boolean }>`
  display: flex;
  justify-content: space-between;
  gap: 24px;
  padding: 0 24px;
`;

const MetricCard = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

const MetricLabel = styled.div`

  font-family: 'Open Sans', sans-serif;
  font-size: 14px;
  font-weight: 600;
  color: #1F2937;
  display: flex;
  align-items: center;
  line-height: 100%;
  gap: 6px;

  svg {
    width: 16px;
    height: 16px;
  }
`;

const MetricValue = styled.div<{ $type: 'average' | 'minimum' | 'maximum' }>`

  font-family: 'Open Sans', sans-serif;
  font-size: 24px;
  font-weight: 700;
  ${({ $type }) => {
    if ($type === 'minimum') {
      return `color: ${theme.colors.success};`;
    }
    if ($type === 'maximum') {
      return `color: ${theme.colors.error};`;
    }
    // For average, use gradient text effect
    return `
      background: ${theme.colors.gradients.primary};
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      background-clip: text;
    `;
  }}
`;

/**
 * Format milliseconds for Y-axis display
 * - Values < 1000ms: show as "X ms"
 * - Values >= 1000ms and < 60000ms: show as "X.X s" (e.g., 1.2s)
 * - Values >= 60000ms: show as "X.X m" (e.g., 1.5m)
 */
const formatResponseTimeForAxis = (valueMs: number): string => {
  if (valueMs < 1000) {
    return `${Math.round(valueMs)} ms`;
  } else if (valueMs < 60000) {
    // Convert to seconds with 1 decimal place
    const seconds = valueMs / 1000;
    // Format to remove trailing zeros (e.g., 1.0 -> 1, 1.2 -> 1.2)
    const formatted = seconds % 1 === 0 ? seconds.toString() : seconds.toFixed(1);
    return `${formatted} s`;
  } else {
    // Convert to minutes with 1 decimal place
    const minutes = valueMs / 60000;
    const formatted = minutes % 1 === 0 ? minutes.toString() : minutes.toFixed(1);
    return `${formatted} m`;
  }
};

/**
 * Format Y-axis ticks with ms/s units
 * - Values < 1000: show as "X ms"
 * - Values >= 1000: show as "X.X s" (e.g., 1.2s)
 */
const formatYAxisTicksWithUnits = (valueMs: number): string => {
  if (valueMs < 1000) {
    return `${Math.round(valueMs)} ms`;
  } else {
    // Convert to seconds with 1 decimal place
    const seconds = valueMs / 1000;
    // Format to remove trailing zeros (e.g., 1.0 -> 1, 1.2 -> 1.2)
    const formatted = seconds % 1 === 0 ? seconds.toString() : seconds.toFixed(1);
    return `${formatted} s`;
  }
};

/**
 * Format milliseconds for metrics display (Average, Minimum, Maximum)
 * - Values < 1000ms: show as "X ms"
 * - Values >= 1000ms: show as "X.X s" (e.g., 1.8 s)
 */
const formatMetricValue = (valueMs: number, metricFormatter?: (value: number) => string): string => {
  if (metricFormatter) {
    return metricFormatter(valueMs);
  }
  if (valueMs < 1000) {
    return `${Math.round(valueMs)} ms`;
  } else {
    const seconds = valueMs / 1000;
    const formatted = seconds % 1 === 0 ? seconds.toString() : seconds.toFixed(1);
    return `${formatted} s`;
  }
};

/**
 * ResponseTimeChart Component
 * 
 * Displays response time chart with metrics
 */
export const ResponseTimeChart: React.FC<ResponseTimeChartProps> = ({
  chartData,
  metrics,
  timeRange = '1h',
  onTimeRangeChange,
  className,
  title = 'Response time',
  subtitle,
  response_time_threshold,
  formatYAxisTicks = true, // Default to true to format ticks with ms/s units
  yAxisLabel = 'Response time',
  tooltipLabel = 'Response time',
  tooltipFormatter,
  yAxisTickFormatter,
  metricFormatter,
  timeWindowButtons,
  dateRangePicker,
  loading = false,
  onFullscreenChange,
}) => {
  const timeRangeOptions = [
    { value: '1h', label: '1H' },
    { value: '24h', label: '1D' },
    { value: '7d', label: '7D' },
    { value: '30d', label: '30D' },
  ];

  // Use provided timeWindowButtons or default to TimeWindowButtons
  const defaultTimeWindowButtons = React.useMemo(() => {
    if (!onTimeRangeChange) return null;

    // Create filters object compatible with TimeWindowButtons
    const filters: DrilldownTableFilters = {
      window: timeRange,
    };

    // Create handler that extracts window value and calls onTimeRangeChange
    const handleFilterApply = (newFilters: DrilldownTableFilters) => {
      if (newFilters.window) {
        onTimeRangeChange(newFilters.window);
      }
    };

    return (
      <TimeWindowButtons
        filters={filters}
        onFilterApply={handleFilterApply}
        timeWindowOptions={timeRangeOptions}
      />
    );
  }, [timeRange, onTimeRangeChange, timeRangeOptions]);

  // Set minimum width to 75% of container
  // ChartWrapper has max-width of 1200px, so 75% = 900px
  // On smaller screens, this will be 75% of actual container width
  const minChartWidth = undefined; // Remove fixed width to prevent overflow - let chart be fully responsive

  // Use custom title/subtitle if provided, otherwise use defaults
  const displayTitle = title || 'Response time';

  // Calculate max value to determine if we should use smart formatting
  const maxValue = Math.max(...chartData.series.flatMap(s => s.data), 0);
  const useSmartFormatting = maxValue >= 1000;

  // Determine Y-axis formatting based on formatYAxisTicks prop
  const yAxisTickFormat = yAxisTickFormatter?yAxisTickFormatter:formatYAxisTicks 
    ? formatYAxisTicksWithUnits 
    : (useSmartFormatting ? formatResponseTimeForAxis : undefined);
  const yAxisTickSuffix = formatYAxisTicks 
    ? undefined 
    : (useSmartFormatting ? undefined : "ms");

  // Ref to measure chart container width
  const chartWrapperRef = React.useRef<HTMLDivElement>(null);
  const [chartWidth, setChartWidth] = React.useState<number>(0);

  // Measure chart width on mount and resize
  React.useEffect(() => {
    const updateWidth = () => {
      if (chartWrapperRef.current) {
        const width = chartWrapperRef.current.clientWidth;
        if (width > 0) {
          setChartWidth(width);
        }
      }
    };

    // Initial measurement with a small delay to ensure DOM is ready
    const timeoutId = setTimeout(() => {
      updateWidth();
    }, 0);

    // Set up ResizeObserver to track width changes
    const resizeObserver = new ResizeObserver(() => {
      updateWidth();
    });

    // Observe the ref element
    const currentRef = chartWrapperRef.current;
    if (currentRef) {
      resizeObserver.observe(currentRef);
    }

    // Also listen to window resize as fallback
    window.addEventListener('resize', updateWidth);

    return () => {
      clearTimeout(timeoutId);
      resizeObserver.disconnect();
      window.removeEventListener('resize', updateWidth);
    };
  }, []);

  // Define maxThresholdPoints based on timeRange for any time range
  // Parses time range string (e.g., "1h", "6h", "24h", "7d", "30d") and calculates dynamically
  const getMaxThresholdPoints = (range: string): number => {
    // Parse time range string to get hours
    const parseHours = (timeRange: string): number => {
      const trimmed = timeRange?.trim().toLowerCase();
      
      // Match patterns like "1h", "6h", "24h" (hours)
      const hourMatch = trimmed.match(/^(\d+(?:\.\d+)?)\s*h(?:ours?)?$/);
      if (hourMatch) {
        return parseFloat(hourMatch[1]);
      }
      
      // Match patterns like "1d", "7d", "30d" (days)
      const dayMatch = trimmed.match(/^(\d+(?:\.\d+)?)\s*d(?:ays?)?$/);
      if (dayMatch) {
        return parseFloat(dayMatch[1]) * 24;
      }
      
      // Match patterns like "1w", "2w" (weeks)
      const weekMatch = trimmed.match(/^(\d+(?:\.\d+)?)\s*w(?:eeks?)?$/);
      if (weekMatch) {
        return parseFloat(weekMatch[1]) * 24 * 7;
      }
      
      // Match patterns like "1m", "2m" (months, approximate as 30 days)
      const monthMatch = trimmed.match(/^(\d+(?:\.\d+)?)\s*m(?:onths?)?$/);
      if (monthMatch) {
        return parseFloat(monthMatch[1]) * 24 * 30;
      }
      
      // Default: try to parse as number (assume hours)
      const numberMatch = trimmed.match(/^(\d+(?:\.\d+)?)$/);
      if (numberMatch) {
        return parseFloat(numberMatch[1]);
      }
      
      // Fallback: return 1 hour if parsing fails
      return 1;
    };
    
    const hours = parseHours(range);
    
    // Calculate maxThresholdPoints using a scaling formula that works for any time range
    // Formula: 10 * hours^0.3 provides good scaling
    // - Shorter ranges get more points per hour (e.g., 1h = 10 points)
    // - Longer ranges get fewer points per hour but more total (e.g., 30d ≈ 100 points)
    // This ensures important threshold points are preserved while scaling appropriately
    const scaledPoints = 10 * Math.pow(hours, 0.3);
    
    // Apply bounds: minimum 10, maximum 200
    // The max can be adjusted based on chart width if needed
    return Math.max(10, Math.min(200, Math.floor(scaledPoints)));
  };

  // Filter chartData based on chart width to prevent overlapping points
  const filteredChartData = React.useMemo(() => {
    const totalPoints = chartData.categories?.length;
    if (totalPoints === 0) return chartData;

    // If width not yet measured, return original data
    if (chartWidth === 0) {
      return chartData;
    }

    // Step 1: Identify all threshold points
    const allThresholdIndices = new Set<number>();
    if (response_time_threshold !== undefined && response_time_threshold !== null) {
      const thresholdLowerBound = response_time_threshold - 100;
      
      chartData.series.forEach(series => {
        series.data.forEach((value, index) => {
          // Keep points that cross threshold or are within 100ms below threshold
          if (value >= thresholdLowerBound) {
            allThresholdIndices.add(index);
          }
        });
      });
    }

    // Step 2: Calculate max points based on chart width
    // Use 0.9 factor to account for spacing and prevent overlap (1 point per pixel with some buffer)
    const maxPoints = Math.max(1, Math.floor(chartWidth * 0.9));
    
    // Calculate interval to fit maxPoints
    // If totalPoints <= maxPoints, we can show all points
    let interval: number;
    if (totalPoints <= maxPoints) {
      interval = 1; // Show all points
    } else {
      // Calculate interval to get approximately maxPoints
      interval = Math.ceil(totalPoints / maxPoints);
    }

    const indicesToKeep = new Set<number>();
    
    // Always include the first point
    if (totalPoints > 0) {
      indicesToKeep.add(0);
    }
    
    // Add points at regular intervals
    for (let i = interval; i < totalPoints - 1; i += interval) {
      indicesToKeep.add(i);
    }
    
    // Always include the last point
    if (totalPoints > 0) {
      indicesToKeep.add(totalPoints - 1);
    }
    
    // Step 3: From threshold points that weren't already included, select by highest value rank
    // Get threshold points that are NOT already in indicesToKeep
    const skippedThresholdIndices = Array.from(allThresholdIndices)
      .filter(index => !indicesToKeep.has(index));
    
    // Get threshold points that ARE already in indicesToKeep (non-skipped threshold points)
    const nonSkippedThresholdIndices = Array.from(allThresholdIndices)
      .filter(index => indicesToKeep.has(index));
    
    if (skippedThresholdIndices?.length > 0) {
      // Sort threshold points by their maximum value across all series (highest first)
      const thresholdPointsWithValues = skippedThresholdIndices.map(index => {
        // Get the maximum value across all series for this index
        const maxValue = Math.max(
          ...chartData.series.map(series => series.data[index] || 0)
        );
        return { index, value: maxValue };
      });
      
      // Sort by value in descending order (highest first)
      thresholdPointsWithValues.sort((a, b) => b.value - a.value);
      
      // Get maxThresholdPoints based on timeRange
      const maxThresholdPoints = getMaxThresholdPoints(timeRange);
      
      // Select top N points by value rank (highest values first)
      const topThresholdPoints = thresholdPointsWithValues
        .slice(0, Math.min(maxThresholdPoints, thresholdPointsWithValues?.length));
      
      const selectedHighestIndices = new Set<number>();
      topThresholdPoints.forEach(({ index }) => {
        indicesToKeep.add(index);
        selectedHighestIndices.add(index);
      });
      
      // Also include any points that crossed the actual threshold (not just threshold - 100)
      // and are NOT within 2 positions of any selected highest value point
      if (response_time_threshold !== undefined && response_time_threshold !== null) {
        skippedThresholdIndices.forEach(index => {
          // Check if this point crossed the actual threshold
          const maxValue = Math.max(
            ...chartData.series.map(series => series.data[index] || 0)
          );
          
          if (maxValue >= response_time_threshold) {
            // Check if this point is NOT within 2 positions of any selected highest value point
            const isNearby = Array.from(selectedHighestIndices).some(selectedIndex => {
              return Math.abs(index - selectedIndex) <= 2;
            });
            
            // If it crossed threshold and is not nearby, include it
            if (!isNearby) {
              indicesToKeep.add(index);
            }
          }
        });
        
        // Step 3.5: Replace low threshold non-skipped points with high threshold skipped points
        // Find non-skipped threshold points that are below the actual threshold (low threshold points)
        const lowThresholdNonSkippedPoints = nonSkippedThresholdIndices
          .map(index => {
            const maxValue = Math.max(
              ...chartData.series.map(series => series.data[index] || 0)
            );
            return { index, value: maxValue };
          })
          .filter(({ value }) => value < response_time_threshold)
          .sort((a, b) => a.index - b.index); // Sort by index for easier processing
        
        // For each low threshold non-skipped point, check if there are high threshold skipped points nearby
        lowThresholdNonSkippedPoints.forEach(({ index: lowIndex, value: lowValue }) => {
          // Find high threshold skipped points near this low threshold point
          // Look for points within a reasonable range (e.g., within n positions)
          const searchRange = Math.max(5, Math.floor(interval * 2)); // Search within 2 intervals or at least 5 positions
          
          const nearbyHighThresholdPoints = thresholdPointsWithValues
            .filter(({ index: highIndex, value: highValue }) => {
              // Check if high threshold point is nearby and above actual threshold
              const distance = Math.abs(highIndex - lowIndex);
              return distance <= searchRange && highValue >= response_time_threshold;
            })
            .sort((a, b) => {
              // Sort by distance first (closer first), then by value (higher first)
              const distA = Math.abs(a.index - lowIndex);
              const distB = Math.abs(b.index - lowIndex);
              if (distA !== distB) {
                return distA - distB;
              }
              return b.value - a.value;
            });
          
          // If we found high threshold points nearby, replace the low threshold point
          if (nearbyHighThresholdPoints?.length > 0) {
            // Remove the low threshold point
            indicesToKeep.delete(lowIndex);
            
            // Add the highest and nearest high threshold point
            const bestReplacement = nearbyHighThresholdPoints[0];
            if (!indicesToKeep.has(bestReplacement.index)) {
              indicesToKeep.add(bestReplacement.index);
              selectedHighestIndices.add(bestReplacement.index);
            }
            
            // Also add other high threshold points that are between the low point and the best replacement
            // but only if they're not too close to the best replacement
            nearbyHighThresholdPoints.slice(1).forEach(({ index: highIndex }) => {
              const isBetween = 
                (highIndex > lowIndex && highIndex < bestReplacement.index) ||
                (highIndex < lowIndex && highIndex > bestReplacement.index);
              
              if (isBetween) {
                const distanceToBest = Math.abs(highIndex - bestReplacement.index);
                // Only add if not too close to the best replacement (at least 2 positions away)
                if (distanceToBest >= 2 && !indicesToKeep.has(highIndex)) {
                  indicesToKeep.add(highIndex);
                }
              }
            });
          }
        });
      }
    }
    
    // Convert to sorted array
    const sortedIndices = Array.from(indicesToKeep).sort((a, b) => a - b);
    
    // Step 4: Group the interval-filtered points by minute and keep only the highest value point per minute
    // Helper function to extract minute-level key from category string
    const getMinuteKey = (category: string): string => {
      try {
        // Try parsing as date string
        const date = new Date(category);
        if (!isNaN(date.getTime())) {
          // Valid date, extract minute-level key (YYYY-MM-DD-HH-MM)
          const year = date.getFullYear();
          const month = String(date.getMonth() + 1).padStart(2, '0');
          const day = String(date.getDate()).padStart(2, '0');
          const hour = String(date.getHours()).padStart(2, '0');
          const minute = String(date.getMinutes()).padStart(2, '0');
          return `${year}-${month}-${day}-${hour}-${minute}`;
        }
      } catch (e) {
        // If parsing fails, try to extract from common formats
      }
      
      // Fallback: try to parse common formats manually
      // Format: "MMM DD, HH:MM" (e.g., "Jan 15, 14:30")
      const longFormatMatch = category.match(/(\w{3})\s+(\d{1,2}),\s+(\d{1,2}):(\d{2})/);
      if (longFormatMatch) {
        const [, monthStr, day, hour, minute] = longFormatMatch;
        const monthMap: { [key: string]: string } = {
          'Jan': '01', 'Feb': '02', 'Mar': '03', 'Apr': '04',
          'May': '05', 'Jun': '06', 'Jul': '07', 'Aug': '08',
          'Sep': '09', 'Oct': '10', 'Nov': '11', 'Dec': '12'
        };
        const month = monthMap[monthStr] || '01';
        const currentYear = new Date().getFullYear();
        return `${currentYear}-${month}-${day.padStart(2, '0')}-${hour.padStart(2, '0')}-${minute}`;
      }
      
      // Format: "HH:MM" (e.g., "14:30") - use current date
      const shortFormatMatch = category.match(/(\d{1,2}):(\d{2})/);
      if (shortFormatMatch) {
        const [, hour, minute] = shortFormatMatch;
        const now = new Date();
        const year = now.getFullYear();
        const month = String(now.getMonth() + 1).padStart(2, '0');
        const day = String(now.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}-${hour.padStart(2, '0')}-${minute}`;
      }
      
      // If all parsing fails, use the category as-is (fallback)
      return category;
    };

    // Group interval-filtered indices by minute
    const minuteGroups = new Map<string, number[]>();
    sortedIndices.forEach(index => {
      const category = chartData.categories[index];
      const minuteKey = getMinuteKey(category);
      if (!minuteGroups.has(minuteKey)) {
        minuteGroups.set(minuteKey, []);
      }
      minuteGroups.get(minuteKey)!.push(index);
    });

    // For each minute group, find the index with the maximum value across all series
    const finalIndices = new Set<number>();
    minuteGroups.forEach((indices) => {
      if (indices?.length === 1) {
        // Only one point in this minute, keep it
        finalIndices.add(indices[0]);
      } else {
        // Multiple points in same minute, find the one with highest value
        let maxValue = -Infinity;
        let maxIndex = indices[0];
        
        indices.forEach(index => {
          // Get the maximum value across all series for this index
          const maxValueAtIndex = Math.max(
            ...chartData.series.map(series => series.data[index] || 0)
          );
          
          if (maxValueAtIndex > maxValue) {
            maxValue = maxValueAtIndex;
            maxIndex = index;
          }
        });
        
        finalIndices.add(maxIndex);
      }
    });
    
    // Convert to sorted array
    let finalSortedIndices = Array.from(finalIndices).sort((a, b) => a - b);
    
    // Step 5: Enforce maxPoints limit if we exceeded it
    // Prioritize: first point, last point, threshold points, then evenly spaced points
    if (finalSortedIndices?.length > maxPoints) {
      const prioritizedIndices = new Set<number>();
      
      // Always keep first and last points
      if (finalSortedIndices?.length > 0) {
        prioritizedIndices.add(finalSortedIndices[0]);
        prioritizedIndices.add(finalSortedIndices[finalSortedIndices?.length - 1]);
      }
      
      // Prioritize threshold points (especially high ones)
      const thresholdIndicesSet = new Set(allThresholdIndices);
      const thresholdIndicesInFinal = finalSortedIndices.filter(index => 
        thresholdIndicesSet.has(index)
      );
      
      // Sort threshold points by value (highest first)
      const thresholdWithValues = thresholdIndicesInFinal.map(index => {
        const maxValue = Math.max(
          ...chartData.series.map(series => series.data[index] || 0)
        );
        return { index, value: maxValue };
      }).sort((a, b) => b.value - a.value);
      
      // Add high threshold points first (up to a reasonable limit)
      const maxThresholdPointsToKeep = Math.min(
        Math.floor(maxPoints * 0.3), // Keep up to 30% of maxPoints as threshold points
        thresholdWithValues?.length
      );
      
      thresholdWithValues.slice(0, maxThresholdPointsToKeep).forEach(({ index }) => {
        prioritizedIndices.add(index);
      });
      
      // Fill remaining slots with evenly spaced points
      const remainingSlots = maxPoints - prioritizedIndices.size;
      if (remainingSlots > 0) {
        const nonPrioritizedIndices = finalSortedIndices.filter(
          index => !prioritizedIndices.has(index)
        );
        
        // Evenly sample from non-prioritized indices
        const spacing = Math.max(1, Math.floor(nonPrioritizedIndices?.length / remainingSlots));
        for (let i = 0; i < nonPrioritizedIndices?.length && prioritizedIndices.size < maxPoints; i += spacing) {
          prioritizedIndices.add(nonPrioritizedIndices[i]);
        }
        
        // If we still have slots, fill them with remaining points
        if (prioritizedIndices.size < maxPoints) {
          for (const index of nonPrioritizedIndices) {
            if (prioritizedIndices.size >= maxPoints) break;
            prioritizedIndices.add(index);
          }
        }
      }
      
      // Convert back to sorted array
      finalSortedIndices = Array.from(prioritizedIndices).sort((a, b) => a - b);
    }
    
    // Filter categories and series data based on final indices
    const filteredCategories = finalSortedIndices.map(index => chartData.categories[index]);
    const filteredSeries = chartData.series.map(series => ({
      ...series,
      data: finalSortedIndices.map(index => series.data[index]),
    }));
    return {
      categories: filteredCategories,
      series: filteredSeries,
    };
  }, [timeRange, chartData, response_time_threshold, chartWidth]);


  return (
    <Container className={className}>
      <ContentGrid>
        <ChartWrapper ref={chartWrapperRef}>
          <LineChart
            data={filteredChartData}
            showArea={false}
            showMarkers={false}
            yAxisTickSuffix={yAxisTickSuffix}
            yAxisTickFormat={yAxisTickFormat}
            xAxisNTicks={5}
            height={250}
            minWidth={minChartWidth}
            response_time_threshold={response_time_threshold}
            yAxisLabel={yAxisLabel}
            tooltipLabel={tooltipLabel}
            tooltipFormatter={tooltipFormatter}
            style={{ border: 'none'}}
            title={displayTitle}
            quickLinks={timeWindowButtons ?? defaultTimeWindowButtons}
            rightActions={dateRangePicker}
            loading={loading}
            onFullscreenChange={onFullscreenChange}
          />
        </ChartWrapper>

        <MetricsGrid $hasTotal={!!metrics.totalChecks}>
      

        <MetricCard>
       
          <MetricValue $type="minimum">{formatMetricValue(metrics.minimum,metricFormatter)}</MetricValue>
          <MetricLabel>
          
          Minimum
        </MetricLabel>
        </MetricCard>
        <MetricCard>
       
       <MetricValue $type="average">{formatMetricValue(metrics.average,metricFormatter)}</MetricValue>
       <MetricLabel>
         Average
       </MetricLabel>
     </MetricCard>

        <MetricCard>
         
          <MetricValue $type="maximum">{formatMetricValue(metrics.maximum,metricFormatter)}</MetricValue>
          <MetricLabel>
         
         Maximum
       </MetricLabel>
        </MetricCard>

        {/* {metrics.totalChecks !== undefined && (
          <MetricCard>
            <MetricLabel>
     
              Total Checks
            </MetricLabel>
            <MetricValue $type="average">{metrics.totalChecks}</MetricValue>
          </MetricCard>
        )} */}
        </MetricsGrid>
      </ContentGrid>
    </Container>
  );
};

export default ResponseTimeChart;

